<template>
  <b-offcanvas
    v-model="forwardVisible"
    no-trap
    placement="end"
    no-header
    bg-variant="sidebar"
    text-variant="sidebar"
    class="form-drawer-sidebar"
    no-backdrop
    :no-close-on-route-change="noCloseOnRouteChange"
    :width="width"
    :style="stackStyle"
  >
    <div class="form-drawer-content">
      <slot></slot>

      <hr/>

      <slot name="footer-above-buttons"></slot>

      <slot name="footer-buttons">
        <b-button
          @click="save"
          :disabled="saveDisabled"
          variant="primary"
          class="action save"
          size="sm"
        >
          <font-awesome-icon :icon="saving ? 'circle-notch' : 'circle-check'" :spin="saving" />
          {{ saveText }}
        </b-button>
        <b-button
          @click="cancel"
          :disabled="cancelDisabled"
          variant="secondary"
          class="action cancel"
          size="sm"
        >
          <font-awesome-icon icon="circle-xmark" />
          {{ cancelText }}
        </b-button>
      </slot>

      <slot name="footer-below-buttons"></slot>

      <mask-spinner v-show="mask" />
    </div>
  </b-offcanvas>
</template>

<script>
import { shallowReactive } from 'vue'
import MaskSpinner from './MaskSpinner.vue'

// Every drawer is an offcanvas panel teleported to <body> with the same z-index, so
// without help they paint in DOM order, which is merely the order they happened to
// mount in (e.g. whichever master-detail list finished loading first). Track the open
// drawers so the most recently opened one is always on top.
const openDrawerIds = shallowReactive([])
let nextDrawerId = 0

// Above the page content (<main> is z-10) but below the app header (z-30), so the
// header's dropdown menus open over a drawer, and below the mobile nav sidebar (z-40).
// Being far below --bs-modal-zindex (1055) also keeps a modal opened from a drawer on top.
const BASE_Z_INDEX = 20

// A drawer nested inside a modal has to clear the modal instead. The same form can be
// rendered on a page or in a modal (e.g. EmployeeForm), so this is detected rather than
// declared. Anything that must in
// turn stay above such a drawer (HoverIcon, DatePicker, IsoDurationSingleInput) starts
// at 1156, which is what caps the stack below.
const MODAL_BASE_Z_INDEX = 1150

// How far a drawer may climb above its base. In practice only a couple of drawers are
// ever open at once, so this is just a safety net keeping us under the next layer up.
const MAX_STACK_OFFSET = 5

const removeFromStack = drawerId => {
  const index = openDrawerIds.indexOf(drawerId)
  if (index >= 0) openDrawerIds.splice(index, 1)
}

export default {
  components: {
    MaskSpinner
  },
  data () {
    return {
      height: undefined,
      drawerId: ++nextDrawerId,
      inModal: false
    }
  },
  props: {
    visible: Boolean,
    saveText: {
      type: String,
      default: 'Save'
    },
    cancelText: {
      type: String,
      default: 'Cancel'
    },
    saveDisabled: {
      type: Boolean,
      default: false
    },
    cancelDisabled: {
      type: Boolean,
      default: false
    },
    mask: {
      type: Boolean,
      default: false
    },
    saving: {
      type: Boolean,
      default: false
    },
    noCloseOnRouteChange: {
      type: Boolean,
      default: false
    },
    width: {
      type: String,
      default: '320px'
    }
  },
  emits: ['cancelForm', 'saveForm', 'update:visible'],
  computed: {
    stackStyle () {
      const index = openDrawerIds.indexOf(this.drawerId)
      if (index < 0) return null
      const base = this.inModal ? MODAL_BASE_Z_INDEX : BASE_Z_INDEX
      // Inline rather than via --bs-offcanvas-zindex, so it also wins against a
      // z-index utility class applied to the drawer.
      return { zIndex: base + Math.min(index, MAX_STACK_OFFSET) }
    },
    forwardVisible: {
			get () {
				return this.visible
			},
			set (v) {
				this.$emit('update:visible', v)
			}
    }
  },
  watch: {
    visible: {
      immediate: true,
      handler (visible) {
        if (!visible) {
          removeFromStack(this.drawerId)
          return
        }
        this.detectInModal()
        if (!openDrawerIds.includes(this.drawerId)) {
          openDrawerIds.push(this.drawerId)
        }
      }
    }
  },
  mounted () {
    this.detectInModal()
  },
  unmounted () {
    removeFromStack(this.drawerId)
  },
  methods: {
    // Raise this drawer above every other open one. Watching `visible` isn't enough on
    // its own: reopening the form for a different row leaves the drawer visible the
    // whole time, so nothing would otherwise move it back to the top of the stack.
    detectInModal () {
      // The panel itself is teleported to <body>, but $el is the teleport's anchor,
      // which stays where the drawer was declared.
      const parent = this.$el?.parentElement
      this.inModal = !!parent?.closest('.modal')
    },
    bringToFront () {
      removeFromStack(this.drawerId)
      openDrawerIds.push(this.drawerId)
    },
    save () {
      this.$emit('saveForm')
    },
    cancel () {
      this.$emit('cancelForm')
    }
  }
}
</script>

<style>
.form-drawer-sidebar.offcanvas {
  /* Start right below the app header (Header.vue publishes its height) and run to the
     bottom of the window. `.offcanvas` already pins the bottom to 0. */
  top: var(--app-header-height, 0px);
  height: auto;

  border-left: 1px solid var(--color-slate-800) !important;
  background-color: var(--color-slate-900) !important;
  color: var(--color-slate-100) !important;

  .form-drawer-content {
    padding: 15px;
    opacity: 1.0;

    /* Specifing global for action selector enables styling for scope overrides */
    .action {
      margin-top: 25px;
      &.cancel {
        margin-left: 10px;
      }
    }
  }

  hr {
    border-color: var(--color-slate-800) !important;
    opacity: 0.3;
  }
}
</style>
